import { useEffect } from "react";
import { Outlet } from "react-router-dom";

import { AgentPanel } from "@/components/agent/AgentPanel";
import { CommandPalette } from "@/components/common/CommandPalette";
import { Header } from "@/components/layout/Header";
import { MobileNav } from "@/components/layout/MobileNav";
import { Sidebar } from "@/components/layout/Sidebar";
import { useSync } from "@/hooks/useSync";
import { useTheme } from "@/hooks/useTheme";
import { useAgentStore } from "@/store/agentStore";

interface AppShellProps {
  variant: "user" | "admin";
}

export function AppShell({ variant }: AppShellProps) {
  useTheme();
  useSync();
  const toggleAgentPanel = useAgentStore((state) => state.togglePanel);

  useEffect(() => {
    if (variant !== "user") {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      if (isTyping) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "j") {
        event.preventDefault();
        toggleAgentPanel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleAgentPanel, variant]);

  return (
    <div className="min-h-screen overflow-x-clip bg-background-primary text-text-primary">
      <div className="flex min-h-screen">
        <Sidebar variant={variant} />
        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          <Header variant={variant} />
          <main className="flex-1 px-4 pb-28 pt-4 sm:px-6 lg:px-8 lg:pb-8 lg:pt-6 2xl:px-10">
            <div className="mx-auto w-full max-w-[1720px] animate-floatIn">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      <MobileNav variant={variant} />
      <CommandPalette variant={variant} />
      {variant === "user" ? <AgentPanel /> : null}
    </div>
  );
}
